/** Client-safe public asset origin. This is not the protected Strapi API URL. */
export const STRAPI_MEDIA_ORIGIN =
  (typeof process !== 'undefined' &&
    process.env.NEXT_PUBLIC_STRAPI_MEDIA_URL?.replace(/\/$/, '')) ||
  '';

type MaybeString = string | null | undefined;

/**
 * Canonical Strapi URL resolver. Use this (directly or via one of the
 * delegating helpers) for every Strapi-served asset URL on the site.
 *
 * Behavior:
 * - `null` / `undefined` / empty / non-string → `null` (caller's job to fallback)
 * - `http://…` / `https://…` → returned as-is (no double-prepending)
 * - `//cdn.example.com/…` (protocol-relative) → coerced to `https:`
 * - `/images/…` (local public asset) → returned as-is
 * - `/uploads/…` (Strapi relative) → prepended with STRAPI_MEDIA_ORIGIN
 * - bare `uploads/…` (no leading slash) → normalized and prepended
 */
export function resolveStrapiMediaUrl(url: MaybeString): string | null {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('//')) return `https:${u}`;
  if (u.startsWith('/images/')) return u;
  if (u.startsWith('/')) return STRAPI_MEDIA_ORIGIN ? `${STRAPI_MEDIA_ORIGIN}${u}` : u;
  const normalized = `/${u.replace(/^\//, '')}`;
  return STRAPI_MEDIA_ORIGIN ? `${STRAPI_MEDIA_ORIGIN}${normalized}` : normalized;
}

/**
 * Turn a Strapi upload URL into a same-origin download URL.
 *
 * Keeping the Strapi origin out of the anchor href prevents PDF clicks from
 * navigating to (and exposing) the CMS host. Non-Strapi URLs are left intact.
 */
export function getStrapiPdfDownloadUrl(url: MaybeString): string | null {
  const resolved = resolveStrapiMediaUrl(url);
  if (!resolved) return null;

  let path: string;

  if (resolved.startsWith('/')) {
    path = resolved;
  } else {
    try {
      const parsed = new URL(resolved);
      const isConfiguredMediaOrigin =
        STRAPI_MEDIA_ORIGIN && parsed.origin === new URL(STRAPI_MEDIA_ORIGIN).origin;
      const isStrapiCloudMedia = /(?:^|\.)media\.strapiapp\.com$/i.test(parsed.hostname);

      if (!isConfiguredMediaOrigin && !isStrapiCloudMedia) return resolved;
      path = parsed.pathname;
    } catch {
      return resolved;
    }
  }

  if (!/\.pdf$/i.test(path)) return resolved;

  return `/api/media/download?path=${encodeURIComponent(path)}`;
}

export type StrapiFormatSize = 'thumbnail' | 'small' | 'medium' | 'large';

interface StrapiFormat {
  url?: string | null;
}

export interface StrapiFlatMedia {
  url?: string | null;
  ext?: string | null;
  mime?: string | null;
  alternativeText?: string | null;
  width?: number | null;
  height?: number | null;
  formats?: {
    thumbnail?: StrapiFormat | null;
    small?: StrapiFormat | null;
    medium?: StrapiFormat | null;
    large?: StrapiFormat | null;
  } | null;
}

type MaybeFlatMedia = StrapiFlatMedia | null | undefined;

const VIDEO_EXTENSION_PATTERN = /\.(mp4|webm|mov|m4v|ogv)(?:$|[?#])/i;

export function isVideoUrl(url: MaybeString): boolean {
  if (!url || typeof url !== 'string') return false;
  return VIDEO_EXTENSION_PATTERN.test(url.trim());
}

export function isStrapiVideoMedia(media: MaybeFlatMedia): boolean {
  if (!media) return false;
  if (media.mime?.toLowerCase().startsWith('video/')) return true;
  if (media.ext && VIDEO_EXTENSION_PATTERN.test(`asset${media.ext}`)) return true;
  return isVideoUrl(media.url);
}

export function isStrapiImageMedia(media: MaybeFlatMedia): boolean {
  if (!media) return false;
  if (media.mime?.toLowerCase().startsWith('image/')) return true;
  return !isStrapiVideoMedia(media) && Boolean(media.url || media.formats);
}

/**
 * Resolve the original Strapi upload URL.
 *
 * The size argument remains in the signature for backwards compatibility with
 * existing section mappers, but generated `formats` are intentionally ignored
 * so every consumer receives the true-quality CMS upload.
 */
export function getStrapiMediaUrl(
  media: MaybeFlatMedia,
  _preferredSize: StrapiFormatSize = 'large',
): string | null {
  if (!media) return null;
  return resolveStrapiMediaUrl(media.url);
}

/**
 * Resolve the original Strapi upload.
 */
export function getStrapiOriginalMediaUrl(media: MaybeFlatMedia): string | null {
  if (!media) return null;
  return resolveStrapiMediaUrl(media.url);
}

/**
 * @deprecated Prefer `getStrapiMediaUrl` (same original-upload semantics,
 * clearer name). Kept for backwards compatibility with existing callers.
 */
export function getStrapiFormatUrl(media: MaybeFlatMedia): string | null {
  return getStrapiMediaUrl(media);
}
